import { FormEvent } from "react";
import { Dispatch } from "@reduxjs/toolkit";
import { redirect } from "react-router-dom";
import { clearError } from "../../redux/slices/ErrorSlice";
import { updateFeedback, updateXHR } from "../../redux/slices/AppState";
import {
  authFailed,
  authStarted,
  authSucceeded,
  loggedOut,
  updateIsVerified,
} from "../../redux/slices/Auth";
import { XHR_STATE } from "../../helpers/xhr";
import { initiateSocketIo } from "../../helpers/socketEvents";
import { FEEDBACK_VARIANT } from "../extras/Alert";
import { authApi, LoginCredentials } from "../../auth/authApi";
import { toAuthError } from "../../auth/authErrors";
import { authStorage } from "../../auth/authStorage";

export type Credentials = LoginCredentials;

const showFeedback = (dispatch: Dispatch<any>, type: string, message: string) =>
  dispatch(updateFeedback({ show: true, type, message }));

export const loginSubmitHandler = async (
  e: FormEvent<HTMLFormElement>,
  data: Credentials,
  dispatch: Dispatch<any>
) => {
  e.preventDefault();
  dispatch(clearError());
  dispatch(authStarted());
  dispatch(updateXHR({ xhr: XHR_STATE.REQUESTING }));

  try {
    const session = await authApi.login(data);
    authStorage.saveSession(session);
    dispatch(authSucceeded(session.user));
    dispatch(updateXHR({ xhr: XHR_STATE.SUCCESS }));

    if (session.user.canPlaceOrder) {
      await initiateSocketIo(true, dispatch).catch(() => undefined);
    }

    if (!session.user.isVerified) {
      showFeedback(
        dispatch,
        FEEDBACK_VARIANT.SUCCESS,
        "Please verify your email to continue."
      );
    }

    return session;
  } catch (error) {
    const authError = toAuthError(error);
    authStorage.clearSession();
    dispatch(authFailed(authError));
    dispatch(updateXHR({ xhr: XHR_STATE.FAILED }));
    showFeedback(dispatch, FEEDBACK_VARIANT.ERROR, authError.message);
    return null;
  }
};

export const resetPasswordHandler = async (
  e: FormEvent<HTMLFormElement>,
  email: string,
  dispatch: Dispatch<any>
) => {
  e.preventDefault();
  dispatch(clearError());
  dispatch(updateXHR({ xhr: XHR_STATE.REQUESTING }));

  try {
    const response = await authApi.requestPasswordReset(email);
    const success = !!response.data.success;
    const message =
      response.data.message ||
      (success
        ? "Password request successful. Please check your email."
        : "Password reset request failed. Please try again.");

    dispatch(
      updateXHR({ xhr: success ? XHR_STATE.SUCCESS : XHR_STATE.FAILED })
    );
    showFeedback(
      dispatch,
      success ? FEEDBACK_VARIANT.SUCCESS : FEEDBACK_VARIANT.ERROR,
      message
    );
  } catch (error) {
    const authError = toAuthError(error);
    dispatch(updateXHR({ xhr: XHR_STATE.FAILED }));
    showFeedback(dispatch, FEEDBACK_VARIANT.ERROR, authError.message);
  }
};

export const logout = async (dispatch: Dispatch<any>) => {
  dispatch(updateXHR({ xhr: XHR_STATE.REQUESTING }));

  try {
    await authApi.logout();
  } catch {
    // Server logout is best-effort. The local session must always be removable.
  } finally {
    authStorage.clearSession();
    dispatch(clearError());
    dispatch(loggedOut());
    dispatch(updateXHR({ xhr: XHR_STATE.SUCCESS }));
  }
};

export const validateAuth = async (_dispatch?: Dispatch<any>) => {
  const token = authStorage.getToken();
  const session = authStorage.getSession();

  if (!token || !session) throw redirect("/");
  if (!session.user.isVerified) throw redirect("/app/email-verification");

  return session;
};

export const verifyEmail = async (code: string, dispatch: Dispatch<any>) => {
  dispatch(clearError());
  dispatch(updateXHR({ xhr: XHR_STATE.REQUESTING }));

  try {
    const response = await authApi.verifyEmail(code);
    if (!response.data.success) {
      throw Object.assign(new Error(response.data.message), {
        response: { status: 400, data: response.data },
      });
    }

    const session = authStorage.getSession();
    if (session) {
      const user = { ...session.user, isVerified: true };
      authStorage.updateUser(user);
    }

    dispatch(updateIsVerified(true));
    dispatch(updateXHR({ xhr: XHR_STATE.SUCCESS }));
    showFeedback(
      dispatch,
      FEEDBACK_VARIANT.SUCCESS,
      "Email verified successfully!"
    );
    return true;
  } catch (error) {
    const authError = toAuthError(error);
    dispatch(updateXHR({ xhr: XHR_STATE.FAILED }));
    showFeedback(dispatch, FEEDBACK_VARIANT.ERROR, authError.message);
    return false;
  }
};
